import { createQueryKeys } from "@lukemorales/query-key-factory";

import type { CoreApiClient } from "../client";
import type { GetBuildingHistoryResponseBody, HistoryEntry, HistoryFilters } from "../types/history.type";

/** Fixed page size for infinite-scroll history (avoids resolving a limit from the full date window). */
export const HISTORY_PAGE_LIMIT = 100;

export const createHistoryQueries = (api: CoreApiClient) => {
  const { request } = api;

  const list = async (
    buildingId: string,
    filters: HistoryFilters,
    cursor?: string,
  ): Promise<GetBuildingHistoryResponseBody> => {
    const { data } = await request("get", "/buildings/{buildingId}/history", {
      params: { path: { buildingId }, query: { ...filters, cursor } },
    });
    return data;
  };

  /** Walks the endpoint's own cursor pagination to exhaustion. How large a page should be is the caller's call. */
  const all = async (buildingId: string, filters: HistoryFilters): Promise<HistoryEntry[]> => {
    const entries: HistoryEntry[] = [];
    let cursor: string | undefined;

    do {
      const page = await list(buildingId, filters, cursor);
      entries.push(...page.data);
      cursor = page.nextCursor;
    } while (cursor);

    return entries;
  };

  return {
    list,
    all,
    queries: createQueryKeys("history", {
      building: (buildingId: string, filters: HistoryFilters) => ({
        queryKey: [buildingId, filters],
        queryFn: () => all(buildingId, filters),
      }),
      buildingPage: (buildingId: string, filters: HistoryFilters) => ({
        queryKey: [buildingId, "page", filters],
        queryFn: ({ pageParam }: { pageParam?: string }) =>
          list(buildingId, { ...filters, limit: HISTORY_PAGE_LIMIT }, pageParam),
      }),
    }),
  };
};
